import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  HStack,
  Input,
  PinInput,
  PinInputField,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import { useSendPhoneOTP, useVerifyPhoneOTP } from '@/api/mutations/useSocialMutations';

interface PhoneVerificationProps {
  onVerified: (phoneNumber: string) => void;
  onSkip?: () => void;
}

export function PhoneVerification({ onVerified, onSkip }: PhoneVerificationProps) {
  const [step, setStep] = useState<'input' | 'verify'>('input');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const toast = useToast();

  const { mutate: sendOTP, isPending: isSending } = useSendPhoneOTP();
  const { mutate: verifyOTP, isPending: isVerifying } = useVerifyPhoneOTP();

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic validation - at least 10 digits
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handleSendOTP = useCallback(() => {
    if (!validatePhoneNumber(phoneNumber)) return;

    sendOTP(
      { phone_number: phoneNumber, country_code: 'US' },
      {
        onSuccess: () => {
          setStep('verify');
          setCountdown(60); // 60 second cooldown before resend
          toast({
            title: 'Code sent!',
            description: 'Check your phone for the verification code',
            status: 'success',
            duration: 3000,
          });
        },
        onError: (error: unknown) => {
          const errorMessage =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message: string }).message)
              : 'Failed to send verification code';
          toast({
            title: 'Error',
            description: errorMessage,
            status: 'error',
            duration: 3000,
          });
        },
      },
    );
  }, [phoneNumber, sendOTP, toast]);

  const handleVerifyOTP = useCallback(() => {
    if (otpCode.length !== 6) return;

    verifyOTP(
      { phone_number: phoneNumber, code: otpCode, country_code: 'US' },
      {
        onSuccess: (data) => {
          toast({
            title: 'Verified!',
            description: 'Your phone number has been verified',
            status: 'success',
            duration: 2000,
          });
          onVerified(data.phone_number);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message: string }).message)
              : 'Please check the code and try again';
          toast({
            title: 'Invalid code',
            description: errorMessage,
            status: 'error',
            duration: 3000,
          });
          setOtpCode('');
        },
      },
    );
  }, [otpCode, phoneNumber, verifyOTP, onVerified, toast]);

  const handleResend = () => {
    setOtpCode('');
    handleSendOTP();
  };

  if (step === 'input') {
    return (
      <VStack gap={4} width="100%">
        <FormControl isInvalid={!!phoneError}>
          <FormLabel color="gray.700">Phone Number</FormLabel>
          <Input
            type="tel"
            placeholder="(555) 123-4567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
            _placeholder={{ color: 'gray.400' }}
          />
          {phoneError && <FormErrorMessage>{phoneError}</FormErrorMessage>}
        </FormControl>

        <HStack width="100%" gap={3}>
          {onSkip && (
            <Button variant="ghost" flex={1} color="gray.500" onClick={onSkip}>
              Skip
            </Button>
          )}
          <Button
            flex={2}
            bg="purple.500"
            color="white"
            onClick={handleSendOTP}
            isLoading={isSending}
            loadingText="Sending..."
            _hover={{ bg: 'purple.600' }}
          >
            Send Code
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack gap={4} width="100%">
      <Text fontSize="md" color="gray.600" textAlign="center">
        Enter the 6-digit code sent to {phoneNumber}
      </Text>

      <HStack justify="center">
        <PinInput
          value={otpCode}
          onChange={setOtpCode}
          otp
          size="lg"
        >
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <PinInputField
            bg="gray.50"
            borderColor="gray.200"
            color="gray.900"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
        </PinInput>
      </HStack>

      <Button
        width="100%"
        bg="purple.500"
        color="white"
        onClick={handleVerifyOTP}
        isLoading={isVerifying}
        isDisabled={otpCode.length !== 6}
        _hover={{ bg: 'purple.600' }}
      >
        Verify
      </Button>

      <HStack gap={2}>
        <Text fontSize="sm" color="gray.500">
          Didn't receive a code?
        </Text>
        <Button
          variant="link"
          size="sm"
          color="purple.500"
          onClick={handleResend}
          isDisabled={countdown > 0}
        >
          {countdown > 0 ? `Resend in ${countdown}s` : 'Resend'}
        </Button>
      </HStack>

      <Button
        variant="ghost"
        size="sm"
        color="gray.500"
        onClick={() => setStep('input')}
      >
        Change phone number
      </Button>
    </VStack>
  );
}

export default PhoneVerification;
